import { Box, Grid, Typography } from "@mui/material";
import theme from "Theme/AppTheme";
import AcxCard from "components/UI/Cards/AcxCard";
import { observer } from "mobx-react";
import React from "react";
import { defaultShadowsForSignals } from "components/Signals/SignalsUtils";
import UniqueCardStore from "./UniqueCardStore";
import { CARD_CONTENT_ATTRIBUTE } from "../../Dashboard/Layout/grid";
import {
    IDriverOnTheRise,
    ISignalsDriversOnTheRiseResponse,
} from "components/Signals/SignalsModels";
import { SignalsTrendingIndicator } from "../SignalsTrendingIndicator";
import PointMenu from "../Drilldown/PointMenu";
import DrilldownDialog from "../Drilldown/DrilldownDialog";
import UniqueCardMenu from "./UniqueCardMenu";
import { ObjectFilterIndicator } from "../ObjectFilters";
import { useCurrentDashboard } from "components/Signals/useCurrentDashboard";
import AcxEmptyState from "components/UI/AcxEmptyStateIndicator/AcxEmptyState";
import { resolveDataAvailability } from "components/Signals/DataAvailability/DataAvailability";
import DataAvailabilityInfo from "components/Signals/DataAvailability/DataAvailabilityInfo";
import { AuthStore } from "stores/AuthStore";
import { useStore } from "utils/useStore";

/**
 * Drivers on the Rise. Mirrors {@link TopicsOnTheRise} in layout — the same four-tile grid, trend
 * indicator, and no-data state — but reports the resolved Primary Driver rather than an actual
 * Primary Topic, and resolves a Driver selection rather than a Topic one.
 *
 * Two deliberate differences from the predecessor:
 *  - No status tag in the header. This Card is tagged New, and the New tag is a filter and
 *    group-by marker only; card headers carry Deprecated and nothing else.
 *  - Theme metadata is optional enrichment and is never rendered. The tile and its point actions
 *    continue to work from Driver identity when no Theme resolves.
 */
const cardRootStyle: React.CSSProperties = {
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: defaultShadowsForSignals,
};

const placeholderDrivers: IDriverOnTheRise[] = Array.from({ length: 4 }).map(
    () => ({
        driverName: " ",
        driverIndex: 0,
        themeName: "",
        themeNumber: 0,
        themeId: "",
        percentChange: 0,
        volume: 0,
        isNew: false,
    }),
);

const DriversOnTheRise = observer(
    ({
        cardStore,
    }: {
        cardStore: UniqueCardStore<ISignalsDriversOnTheRiseResponse>;
    }) => {
        const authStore = useStore(AuthStore);
        const currentDashboard = useCurrentDashboard();
        const [pointMenuOpen, setPointMenuOpen] = React.useState(false);
        const [pointMenuAnchor, setPointMenuAnchor] = React.useState<
            { top: number; left: number } | undefined
        >(undefined);

        const isLoading = cardStore.isRefreshing;

        function handleOnClick(
            event: React.MouseEvent<HTMLDivElement>,
            driverData: IDriverOnTheRise,
        ) {
            if (
                cardStore.isPresentationMode ||
                isLoading ||
                !cardStore.isPointMenuEnabled ||
                !cardStore.drilldownStore
            )
                return;

            cardStore.drilldownStore.clearSelection();
            cardStore.drilldownStore.selectDriver({
                selectedDriver: driverData.driverName,
                driverIndex: driverData.driverIndex,
                themeNumber: driverData.themeNumber || undefined,
                themeId: driverData.themeId || undefined,
                x: driverData.driverName,
                count:
                    typeof driverData.volume === "number"
                        ? driverData.volume
                        : Number(driverData.volume) || null,
            });
            setPointMenuAnchor({
                top: event.clientY,
                left: event.clientX,
            });
            setPointMenuOpen(true);
        }

        const handlePointMenuClose = () => {
            setPointMenuOpen(false);
        };

        const hasData = (cardStore.data?.data.length ?? 0) > 0;
        const driversToRender =
            isLoading || hasData
                ? hasData && !isLoading
                    ? cardStore.data?.data ?? []
                    : cardStore.data?.data?.length
                    ? cardStore.data.data
                    : placeholderDrivers
                : [];
        const shouldShowNoData = !isLoading && driversToRender.length === 0;
        const dataAvailability = resolveDataAvailability({
            authStore,
            policy: cardStore.config.dataAvailability,
            defaultEmptyStateVariant:
                cardStore.config.renderOptions.emptyStateVariant,
            startDate: cardStore.state?.startDate,
            endDate: cardStore.state?.endDate,
            hasRenderableData: hasData,
        });
        const showDataAvailabilityInfo =
            !!dataAvailability.infoKind &&
            !isLoading &&
            !cardStore.error &&
            !cardStore.isPresentationMode &&
            !currentDashboard?.isExportCaptureInProgress;
        const showMenuButton =
            !cardStore.isPresentationMode &&
            cardStore.config.renderOptions.menu;
        const cardTitle = cardStore.title || "Drivers on the Rise";
        const cardSubtitle = cardStore.subtitle;

        return (
            <Grid sx={{ height: "100%" }}>
                <Grid
                    item
                    container
                    xs={12}
                    // Fills the card, so the layout measures its rows instead.
                    {...{ [CARD_CONTENT_ATTRIBUTE]: "" }}
                    sx={{
                        paddingLeft: theme.spacing(2),
                        paddingRight: theme.spacing(2),
                        paddingBottom: theme.spacing(2),
                        backgroundColor: theme.palette.primary[800],
                        borderRadius: 2,
                        height: "100%",
                        alignContent: "flex-start",
                    }}
                >
                    <Grid
                        item
                        container
                        xs={12}
                        sx={{
                            paddingLeft: theme.spacing(1),
                            paddingTop: theme.spacing(2),
                            paddingBottom: theme.spacing(2),
                        }}
                        gap={1}
                        alignItems={"center"}
                        justifyContent={
                            showMenuButton ? "space-between" : "flex-start"
                        }
                    >
                        <Grid item xs>
                            <Grid container alignItems="center" gap={1}>
                                <Typography
                                    sx={{
                                        color: theme.palette.white.main,
                                        fontWeight: 600,
                                        fontSize: "16px",
                                        lineHeight: "24px",
                                    }}
                                >
                                    {cardTitle}
                                </Typography>
                                {showDataAvailabilityInfo &&
                                    dataAvailability.infoKind && (
                                        <Grid item className="no-export">
                                            <DataAvailabilityInfo
                                                kind={dataAvailability.infoKind}
                                                color={theme.palette.white.main}
                                            />
                                        </Grid>
                                    )}
                            </Grid>
                            {!!cardSubtitle && (
                                <Typography
                                    sx={{
                                        color: theme.palette.white.main,
                                        opacity: 0.9,
                                        fontSize: "13px",
                                        lineHeight: "18px",
                                    }}
                                >
                                    {cardSubtitle}
                                </Typography>
                            )}
                        </Grid>
                        {showMenuButton && (
                            <Grid
                                item
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                <ObjectFilterIndicator store={cardStore} />
                                <UniqueCardMenu
                                    store={cardStore}
                                    iconButtonStyle={{
                                        color: theme.palette.white.main,
                                        border: "none",
                                        backgroundColor: "transparent",
                                    }}
                                />
                            </Grid>
                        )}
                    </Grid>
                    {!shouldShowNoData ? (
                        <Box
                            sx={{
                                width: "100%",
                                display: "grid",
                                gridTemplateColumns:
                                    "repeat(auto-fit, minmax(240px, 1fr))",
                                gap: theme.spacing(2),
                                marginBottom: theme.spacing(1),
                            }}
                        >
                            {driversToRender.map((i, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        zIndex: 1000, // keep spinner below header
                                        "&:hover":
                                            !cardStore.isPresentationMode &&
                                            !isLoading
                                                ? { cursor: "pointer" }
                                                : undefined,
                                    }}
                                    onClick={(e) => handleOnClick(e, i)}
                                    data-testid="drivers-on-the-rise-item"
                                >
                                    <AcxCard
                                        loading={isLoading}
                                        loadingSize={24}
                                        title={
                                            <Typography
                                                fontWeight="600"
                                                fontSize="16px"
                                                color={theme.palette.gray[500]}
                                                lineHeight="1.5"
                                            >
                                                {isLoading ? " " : i.driverName}
                                            </Typography>
                                        }
                                        rootStyle={cardRootStyle}
                                        headerStyle={{ paddingBottom: 0 }}
                                        mainContentStyle={{
                                            justifyContent: "start",
                                            paddingTop: 0,
                                            paddingBottom: theme.spacing(2),
                                        }}
                                        mainContent={
                                            <Grid
                                                container
                                                item
                                                xs={12}
                                                justifyContent={"space-between"}
                                                alignItems="center"
                                            >
                                                <Typography
                                                    fontWeight="600"
                                                    fontSize="20px"
                                                    lineHeight="32px"
                                                    color={
                                                        theme.palette.gray[600]
                                                    }
                                                    data-testid="drivers-item-volume"
                                                >
                                                    {i.volume}
                                                </Typography>
                                                <Grid container item xs={10}>
                                                    <SignalsTrendingIndicator
                                                        inputValue={
                                                            i.percentChange
                                                        }
                                                        isNew={i.isNew}
                                                        showGrowthSubtitle
                                                        isNegativeRed
                                                    />
                                                </Grid>
                                            </Grid>
                                        }
                                    />
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Grid
                            item
                            container
                            xs={12}
                            style={{ color: theme.palette.white.main }}
                        >
                            <AcxEmptyState
                                size="small"
                                variant={dataAvailability.emptyStateVariant}
                            />
                        </Grid>
                    )}
                </Grid>
                <PointMenu
                    store={cardStore}
                    open={pointMenuOpen}
                    anchorPosition={pointMenuAnchor}
                    onClose={handlePointMenuClose}
                />
                <DrilldownDialog
                    baseObjectStore={cardStore}
                    drilldownStore={cardStore.drilldownStore}
                />
            </Grid>
        );
    },
);

export default DriversOnTheRise;
